import { useApolloClient } from '@apollo/client';
import type { PortfolioReport } from '@interfaces/portfolioReport';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  type ResolvePortfolioReportsByIdsOptions,
  resolvePortfolioReportsByIds,
} from '../utils/reports/resolvePortfolioReportsByIds';

export const useFailedPortfolioReportLookup = (
  organizationId: string | undefined,
  missingReportIds: string[],
  options: ResolvePortfolioReportsByIdsOptions = {},
): {
  failedReports: PortfolioReport[];
  reportsById: Map<string, PortfolioReport>;
  loading: boolean;
} => {
  const client = useApolloClient();
  const [resolvedById, setResolvedById] = useState<
    Map<string, PortfolioReport>
  >(() => new Map());
  const [loading, setLoading] = useState(false);
  const inFlightRef = useRef<Set<string>>(new Set());

  const pendingIds = useMemo(
    () => missingReportIds.filter((id) => !resolvedById.has(id)),
    [missingReportIds, resolvedById],
  );
  const pendingKey = pendingIds.join(',');
  const optimizerRunId = options.optimizerRunId;

  useEffect(() => {
    if (!organizationId || pendingIds.length === 0) {
      setLoading(false);
      return;
    }

    const idsToFetch = pendingIds.filter((id) => !inFlightRef.current.has(id));
    if (idsToFetch.length === 0) {
      return;
    }

    let cancelled = false;
    setLoading(true);
    idsToFetch.forEach((id) => inFlightRef.current.add(id));

    resolvePortfolioReportsByIds(client, organizationId, idsToFetch, {
      optimizerRunId,
      preferDirectRefresh: true,
    })
      .then((resolved) => {
        setResolvedById((current) => {
          const next = new Map(current);
          for (const [id, report] of resolved.entries()) {
            next.set(id, report);
          }
          return next;
        });
      })
      .finally(() => {
        idsToFetch.forEach((id) => inFlightRef.current.delete(id));
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [client, organizationId, optimizerRunId, pendingKey]);

  const failedReports = useMemo(
    () => [...resolvedById.values()],
    [resolvedById],
  );

  return {
    failedReports,
    reportsById: resolvedById,
    loading,
  };
};
